import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * The account cluster's menu — a popover of headings, choices and actions.
 *
 * A second menu shape, and deliberately not `RowMenu`: that one is a flat list of actions on
 * a row, this one is sections — who you are, then two single-choice groups, then the things
 * you can do. Both are React Aria's `MenuTrigger` underneath, so opening, Escape, the click
 * outside, arrow keys, typeahead and the focus that returns to the trigger are RAC's, and a
 * choice group announces as `menuitemradio` rather than through hand-written `aria-checked`.
 *
 * It lives here because `.menu-pop`, `.m-head`, `.menu-item` and `.menu-sep` are the sheet's
 * — the app composes these and never reaches for RAC itself.
 */
import { Header, Menu, MenuItem, MenuSection, MenuTrigger, Popover, Separator, } from 'react-aria-components';
export function MenuButton({ trigger, label, children, footer }) {
    return (_jsxs(MenuTrigger, { children: [_jsx("div", { className: "menu-anchor", children: trigger }), _jsxs(Popover, { className: "menu-pop", placement: "bottom end", offset: 8, children: [_jsx(Menu, { className: "menu-list", "aria-label": label, children: children }), footer] })] }));
}
/**
 * A mono caption above a group. Not an item: there is nothing to choose here.
 *
 * React Aria's `Header`, and not a `<span>`: a menu is a **collection**, so its children are
 * read into a collection document before they are rendered, and a bare element there is not
 * a node that document knows how to build.
 */
export function MenuHeading({ children }) {
    return _jsx(Header, { className: "m-head", children: children });
}
/** A hairline between groups. */
export function MenuSeparator() {
    return _jsx(Separator, { className: "menu-sep" });
}
/** A group of items that is not a choice — a heading, then actions. */
export function MenuGroup({ children }) {
    return _jsx(MenuSection, { className: "menu-group", children: children });
}
/**
 * A short note in the menu, with its own actions — the unverified-email line.
 *
 * **A note is not a heading.** It said its two sentences as two `MenuHeading`s until
 * `studio/13`, which read as two captions of nothing and ran long in Turkish. It is a
 * tinted block now: the lines in `text`, then its actions beneath them as links.
 *
 * Still a `MenuSection`, so the actions stay real menu items — arrow keys reach them and a
 * reader hears the note as their group's name. Only the box and the type are new.
 */
export function MenuNote({ text, children, }) {
    return (_jsxs(MenuSection, { className: "a-note", children: [_jsx(Header, { className: "a-text", children: text }), children] }));
}
/** One thing you can do. */
export function MenuAction({ id, onAction, children, }) {
    return (_jsx(MenuItem, { id: id, className: "menu-item", onAction: onAction, children: children }));
}
/**
 * A setting under the menu, as one control rather than a row per value (`i-0009`).
 *
 * `MenuChoice` below spends a menu item on every option, which is right for a short list of
 * *actions* and wrong for a setting you only ever flip: two languages and three themes cost
 * five rows of a menu that also has to hold an account, a password and a way out.
 *
 * It goes in `MenuButton`'s `footer`, not among the items — see the note there. A menu keeps
 * its keyboard to itself, so a control among its items can only be clicked.
 */
export function MenuControl({ heading, children, }) {
    return (_jsxs("div", { className: "m-set", children: [_jsx("span", { className: "m-head", children: heading }), children] }));
}
/** Exactly one of these is true — a radio group, said the way ARIA says it. */
export function MenuChoice({ heading, value, options, onChange, }) {
    return (_jsxs(MenuSection, { className: "menu-group", selectionMode: "single", disallowEmptySelection: true, selectedKeys: [value], onSelectionChange: (keys) => {
            const [first] = [...keys];
            if (first !== undefined)
                onChange(String(first));
        }, children: [_jsx(MenuHeading, { children: heading }), options.map((option) => (_jsx(MenuItem, { id: option.value, className: "menu-item", children: option.label }, option.value)))] }));
}
//# sourceMappingURL=Menu.js.map